import { filterItems } from "../helpers/filter-items.js";
import SidebarItemDecorator_default from "./SidebarItemDecorator.vue.js";
import { hasChildren } from "../helpers/has-children.js";
import { isSidebarFolder } from "../helpers/is-sidebar-folder.js";
import { useDraggable } from "../hooks/use-draggable.js";
import SidebarHttpBadge_default from "./SidebarHttpBadge.vue.js";
import SidebarItemLabel_default from "./SidebarItemLabel.vue.js";
import { Fragment, computed, createBlock, createCommentVNode, createElementBlock, createSlots, createTextVNode, createVNode, defineComponent, mergeProps, normalizeClass, openBlock, renderList, renderSlot, resolveComponent, toDisplayString, toHandlers, unref, useSlots, withCtx } from "vue";
import { ScalarSidebarButton, ScalarSidebarGroup, ScalarSidebarGroupToggle, ScalarSidebarGroupToggleButton, ScalarSidebarGroupToggleSpacer, ScalarSidebarIndent, ScalarSidebarItem, ScalarSidebarSection } from "@scalar/components/sidebar";
import { isPlainLeftClick } from "@scalar/helpers/dom/is-plain-left-click";
import { LibraryIcon } from "@scalar/icons/library";
//#region src/components/SidebarItem.vue?vue&type=script&setup=true&lang.ts
var _hoisted_1 = {
	key: 0,
	class: "line-through"
};
var _hoisted_2 = {
	key: 0,
	class: "line-through"
};
var SidebarItem_vue_vue_type_script_setup_true_lang_default = /*@__PURE__*/ defineComponent({
	__name: "SidebarItem",
	props: {
		item: {},
		layout: {},
		isSelected: { type: Function },
		isExpanded: { type: Function },
		options: {},
		getHref: { type: Function },
		isDraggable: {},
		isDroppable: { type: Function }
	},
	emits: [
		"selectItem",
		"onDragEnd",
		"toggleGroup"
	],
	setup(__props, { emit: __emit }) {
		const emit = __emit;
		const slots = useSlots();
		const isGroup = (currentItem) => {
			return "isGroup" in currentItem && currentItem.isGroup;
		};
		const isDeprecated = (currentItem) => {
			return ("isDeprecated" in currentItem && currentItem.isDeprecated) ?? false;
		};
		/**
		* Handle drag end event and bubble it up to parent.
		*/
		const onDragEnd = (draggingItem, hoveredItem) => {
			emit("onDragEnd", draggingItem, hoveredItem);
		};
		const { draggableAttrs, draggableEvents } = useDraggable({
			id: __props.item.id,
			isDraggable: __props.isDraggable,
			isDroppable: __props.isDroppable,
			onDragEnd
		});
		const children = computed(() => hasChildren(__props.item) ? filterItems(__props.layout, __props.item.children, __props.options?.hideOperationDefaultExamples) : []);
		/**
		* Whether this item is the one currently displayed on the page.
		*
		* `isSelected` also reports true for every ancestor of the displayed item,
		* which is what the active styling of a group needs. Only the deepest
		* selected node is the current page though, so a group counts as current when
		* none of its children are selected. Leaf items already get this from the
		* `selected` prop of ScalarSidebarButton.
		*/
		const isCurrent = computed(() => __props.isSelected(__props.item.id) && !(hasChildren(__props.item) && __props.item.children.some((child) => __props.isSelected(child.id))));
		/** The URL for this item, when the consumer provides one it renders as a link */
		const href = computed(() => __props.getHref?.(__props.item) || void 0);
		/**
		* Whether the group renders its toggle separately from its label.
		*
		* A label that navigates cannot also own the open state, so a group that
		* renders as a link needs a toggle button of its own. Without one there is no
		* way to collapse the group from the keyboard, because pressing Enter on the
		* label follows the link instead.
		*/
		const isDiscrete = computed(() => Boolean(href.value) || __props.layout === "reference" && __props.item.type === "text");
		/**
		* Builds the screen reader label for the discrete group toggle.
		*
		* The title of the group is appended so the toggles can be told apart. A page
		* can hold a lot of them, and a list of identical "Open Group" buttons gives a
		* screen reader user nothing to navigate by.
		*/
		const getToggleLabel = (open) => {
			const label = open ? __props.options?.labels?.closeGroup ?? "Close Group" : __props.options?.labels?.openGroup ?? "Open Group";
			return __props.item.title ? `${label} - ${__props.item.title}` : label;
		};
		/**
		* Emit the select event for a click on the item label.
		*
		* When the item renders as a link we only hijack plain left clicks on the
		* link itself for in-app navigation. Modified clicks (meta, ctrl, shift,
		* alt) and middle clicks keep the browser's default behavior so the link
		* can be opened in a new tab or window, and clicks on surrounding content
		* (like the decorator menu) keep their native behavior. A click whose
		* default is already prevented has been handled by someone else, so it is
		* left alone rather than navigated a second time.
		*/
		const handleSelect = (event) => {
			if (href.value) {
				if ((event.target instanceof Element ? event.target.closest("a") : null)?.getAttribute("href") !== href.value) return;
				if (event.defaultPrevented || !isPlainLeftClick(event)) return;
				event.preventDefault();
			}
			emit("selectItem", __props.item.id);
		};
		return (_ctx, _cache) => {
			const _component_SidebarItem = resolveComponent("SidebarItem", true);
			return __props.layout === "client" && children.value.length > 0 && isGroup(__props.item) ? (openBlock(), createBlock(unref(ScalarSidebarSection), mergeProps({
				key: 0,
				"data-sidebar-id": __props.item.id
			}, unref(draggableAttrs), toHandlers(unref(draggableEvents))), {
				items: withCtx(() => [(openBlock(true), createElementBlock(Fragment, null, renderList(children.value, (child) => {
					return openBlock(), createBlock(_component_SidebarItem, {
						key: child.id,
						getHref: __props.getHref,
						isDraggable: __props.isDraggable,
						isDroppable: __props.isDroppable,
						isExpanded: __props.isExpanded,
						isSelected: __props.isSelected,
						item: child,
						layout: __props.layout,
						options: __props.options,
						onOnDragEnd: onDragEnd,
						onSelectItem: _cache[0] || (_cache[0] = (id) => emit("selectItem", id)),
						onToggleGroup: _cache[1] || (_cache[1] = (id) => emit("toggleGroup", id))
					}, createSlots({ _: 2 }, [
						slots.decorator ? {
							name: "decorator",
							fn: withCtx((slotProps) => [renderSlot(_ctx.$slots, "decorator", mergeProps({ ref_for: true }, slotProps))]),
							key: "0"
						} : void 0,
						slots.empty ? {
							name: "empty",
							fn: withCtx((slotProps) => [renderSlot(_ctx.$slots, "empty", mergeProps({ ref_for: true }, slotProps))]),
							key: "1"
						} : void 0,
						slots.icon ? {
							name: "icon",
							fn: withCtx((slotProps) => [renderSlot(_ctx.$slots, "icon", mergeProps({ ref_for: true }, slotProps))]),
							key: "2"
						} : void 0
					]), 1032, [
						"getHref",
						"isDraggable",
						"isDroppable",
						"isExpanded",
						"isSelected",
						"item",
						"layout",
						"options"
					]);
				}), 128))]),
				default: withCtx(() => [createTextVNode(toDisplayString(__props.item.title) + " ", 1)]),
				_: 3
			}, 16, ["data-sidebar-id"])) : unref(isSidebarFolder)(__props.layout, __props.item, slots.empty !== void 0, __props.options?.hideOperationDefaultExamples ?? false) ? (openBlock(), createBlock(unref(ScalarSidebarGroup), mergeProps({
				key: 1,
				active: __props.isSelected(__props.item.id),
				class: "relative",
				controlled: "",
				"data-sidebar-id": __props.item.id
			}, unref(draggableAttrs), {
				discrete: isDiscrete.value,
				open: __props.isExpanded(__props.item.id)
			}, toHandlers(unref(draggableEvents))), createSlots({
				button: withCtx(({ open, level }) => [createVNode(unref(ScalarSidebarButton), {
					is: href.value ? "a" : "button",
					active: __props.isSelected(__props.item.id),
					"aria-current": isCurrent.value ? "page" : void 0,
					"aria-expanded": isDiscrete.value ? void 0 : open,
					href: href.value,
					indent: level,
					onClick: handleSelect
				}, createSlots({
					indent: withCtx(() => [createVNode(unref(ScalarSidebarIndent), {
						class: "-my-2 mr-0",
						indent: level
					}, null, 8, ["indent"])]),
					aside: withCtx(() => ["method" in __props.item ? (openBlock(), createBlock(SidebarHttpBadge_default, {
						key: 0,
						active: __props.isSelected(__props.item.id),
						class: normalizeClass(["ml-2 h-4 self-start", { "group-hover/button:opacity-0 group-focus-visible/button:opacity-0 group-has-[~*_[aria-expanded=true]]/button:opacity-0 group-has-[~*:focus-within]/button:opacity-0 group-has-[~*:hover]/button:opacity-0": slots.decorator }]),
						label: __props.options?.labels?.httpMethod,
						method: __props.item.method,
						webhook: __props.item.type === "webhook"
					}, null, 8, [
						"active",
						"class",
						"label",
						"method",
						"webhook"
					])) : createCommentVNode("", true), isDiscrete.value ? (openBlock(), createBlock(unref(ScalarSidebarGroupToggleSpacer), { key: 1 })) : !("method" in __props.item) ? (openBlock(), createBlock(unref(ScalarSidebarGroupToggle), {
						key: 2,
						class: "text-sidebar-c-2",
						open
					}, {
						label: withCtx(() => [createTextVNode(toDisplayString(open ? __props.options?.labels?.closeGroup ?? "Close Group" : __props.options?.labels?.openGroup ?? "Open Group"), 1)]),
						_: 2
					}, 1032, ["open"])) : createCommentVNode("", true)]),
					default: withCtx(() => [isDeprecated(__props.item) ? (openBlock(), createElementBlock("span", _hoisted_1, [createVNode(SidebarItemLabel_default, {
						item: __props.item,
						operationTitleSource: __props.options?.operationTitleSource
					}, null, 8, ["item", "operationTitleSource"])])) : (openBlock(), createBlock(SidebarItemLabel_default, {
						key: 1,
						item: __props.item,
						operationTitleSource: __props.options?.operationTitleSource
					}, null, 8, ["item", "operationTitleSource"]))]),
					_: 2
				}, [__props.item.type === "document" ? {
					name: "icon",
					fn: withCtx(() => [renderSlot(_ctx.$slots, "icon", {
						item: __props.item,
						open
					}, () => [createVNode(unref(LibraryIcon), {
						class: "block",
						src: "icon" in __props.item && __props.item.icon || "interface-content-folder"
					}, null, 8, ["src"])])]),
					key: "0"
				} : void 0]), 1032, [
					"is",
					"active",
					"aria-current",
					"aria-expanded",
					"href",
					"indent"
				]), isDiscrete.value ? (openBlock(), createBlock(unref(ScalarSidebarGroupToggleButton), {
					key: 0,
					open,
					onClick: _cache[2] || (_cache[2] = () => emit("toggleGroup", __props.item.id))
				}, {
					label: withCtx(() => [createTextVNode(toDisplayString(getToggleLabel(open)), 1)]),
					_: 2
				}, 1032, ["open"])) : createCommentVNode("", true)]),
				items: withCtx(() => [(openBlock(true), createElementBlock(Fragment, null, renderList(children.value, (child) => {
					return openBlock(), createBlock(_component_SidebarItem, {
						key: child.id,
						getHref: __props.getHref,
						isDraggable: __props.isDraggable,
						isDroppable: __props.isDroppable,
						isExpanded: __props.isExpanded,
						isSelected: __props.isSelected,
						item: child,
						layout: __props.layout,
						options: __props.options,
						parentIds: [],
						onOnDragEnd: onDragEnd,
						onSelectItem: _cache[3] || (_cache[3] = (id) => emit("selectItem", id)),
						onToggleGroup: _cache[4] || (_cache[4] = (id) => emit("toggleGroup", id))
					}, createSlots({ _: 2 }, [
						slots.decorator ? {
							name: "decorator",
							fn: withCtx((slotProps) => [renderSlot(_ctx.$slots, "decorator", mergeProps({ ref_for: true }, slotProps))]),
							key: "0"
						} : void 0,
						slots.empty ? {
							name: "empty",
							fn: withCtx((slotProps) => [renderSlot(_ctx.$slots, "empty", mergeProps({ ref_for: true }, slotProps))]),
							key: "1"
						} : void 0,
						slots.icon ? {
							name: "icon",
							fn: withCtx((slotProps) => [renderSlot(_ctx.$slots, "icon", mergeProps({ ref_for: true }, slotProps))]),
							key: "2"
						} : void 0
					]), 1032, [
						"getHref",
						"isDraggable",
						"isDroppable",
						"isExpanded",
						"isSelected",
						"item",
						"layout",
						"options"
					]);
				}), 128)), slots.empty && (__props.item.children?.length ?? 0) === 0 ? renderSlot(_ctx.$slots, "empty", { item: __props.item }, void 0, void 0, 0) : createCommentVNode("", true)]),
				_: 2
			}, [slots.decorator ? {
				name: "after",
				fn: withCtx(() => [createVNode(SidebarItemDecorator_default, null, {
					default: withCtx(() => [renderSlot(_ctx.$slots, "decorator", { item: __props.item })]),
					_: 3
				})]),
				key: "0"
			} : void 0]), 1040, [
				"active",
				"data-sidebar-id",
				"discrete",
				"open"
			])) : (openBlock(), createBlock(unref(ScalarSidebarItem), mergeProps({ key: 2 }, unref(draggableAttrs), {
				class: "relative",
				"data-sidebar-id": __props.item.id
			}, toHandlers(unref(draggableEvents)), { onClick: handleSelect }), createSlots({
				button: withCtx(({ level }) => [createVNode(unref(ScalarSidebarButton), {
					is: href.value ? "a" : "button",
					href: href.value,
					indent: level,
					selected: __props.isSelected(__props.item.id)
				}, createSlots({
					default: withCtx(() => [isDeprecated(__props.item) ? (openBlock(), createElementBlock("span", _hoisted_2, [createVNode(SidebarItemLabel_default, {
						item: __props.item,
						operationTitleSource: __props.options?.operationTitleSource
					}, null, 8, ["item", "operationTitleSource"])])) : (openBlock(), createBlock(SidebarItemLabel_default, {
						key: 1,
						item: __props.item,
						operationTitleSource: __props.options?.operationTitleSource
					}, null, 8, ["item", "operationTitleSource"]))]),
					_: 2
				}, [slots.icon ? {
					name: "icon",
					fn: withCtx(() => [renderSlot(_ctx.$slots, "icon", {
						item: __props.item,
						open: true
					})]),
					key: "0"
				} : void 0, "method" in __props.item ? {
					name: "aside",
					fn: withCtx(() => [createVNode(SidebarHttpBadge_default, {
						active: __props.isSelected(__props.item.id),
						class: normalizeClass(["ml-2 h-4 self-start", { "group-hover/button:opacity-0 group-focus-visible/button:opacity-0 group-has-[~*_[aria-expanded=true]]/button:opacity-0 group-has-[~*:focus-within]/button:opacity-0 group-has-[~*:hover]/button:opacity-0": slots.decorator }]),
						label: __props.options?.labels?.httpMethod,
						method: __props.item.method,
						webhook: __props.item.type === "webhook"
					}, null, 8, [
						"active",
						"class",
						"label",
						"method",
						"webhook"
					])]),
					key: "1"
				} : void 0]), 1032, [
					"is",
					"href",
					"indent",
					"selected"
				])]),
				_: 2
			}, [slots.decorator ? {
				name: "after",
				fn: withCtx(() => [createVNode(SidebarItemDecorator_default, null, {
					default: withCtx(() => [renderSlot(_ctx.$slots, "decorator", { item: __props.item })]),
					_: 3
				})]),
				key: "0"
			} : void 0]), 1040, ["data-sidebar-id"]));
		};
	}
});
//#endregion
export { SidebarItem_vue_vue_type_script_setup_true_lang_default as default };

//# sourceMappingURL=SidebarItem.vue.script.js.map